import i18n from '../i18n';
import { apiRequest } from './http';
import type {
  CategoryListResponse,
  CategorySummary,
  CreateTemplateRequest,
  TemplateDetail,
  TemplateListResponse,
  TemplateSummary,
  PropertyTypeDefinition,
  SavePropertyRequest,
  SystemPropertySummary,
  TemplatePropertySummary,
  UpdateTemplateRequest,
  VariableCatalogue
} from './types';

const templatesPath = '/api/manage/templates';
const categoriesPath = '/api/manage/categories';

export async function createTemplate(request: CreateTemplateRequest): Promise<TemplateDetail> {
  return apiRequest<TemplateDetail>(templatesPath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  });
}

/** Kept in sync with the Kestrel MaxRequestBodySize override in api/Program.cs. */
export const MAX_UPLOAD_FILE_SIZE_BYTES = 100 * 1024 * 1024;

/** The API takes the file as base64 so an upload is a single JSON request. */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      reject(new Error(i18n.t('errors.fileTooLarge', { maxSizeMb: MAX_UPLOAD_FILE_SIZE_BYTES / (1024 * 1024) })));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(reader.error?.message ?? 'Unable to read file'));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

export async function listTemplates(): Promise<TemplateSummary[]> {
  const response = await apiRequest<TemplateListResponse>(templatesPath);
  return response.items ?? [];
}

export async function getTemplate(templateId: string): Promise<TemplateDetail> {
  return apiRequest<TemplateDetail>(`${templatesPath}/${encodeURIComponent(templateId)}`);
}

export async function updateTemplate(
  templateId: string,
  changes: UpdateTemplateRequest
): Promise<TemplateDetail> {
  return apiRequest<TemplateDetail>(`${templatesPath}/${encodeURIComponent(templateId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes)
  });
}

export async function deleteTemplate(templateId: string): Promise<void> {
  await apiRequest(`${templatesPath}/${encodeURIComponent(templateId)}`, { method: 'DELETE' });
}

export async function listCategories(): Promise<CategorySummary[]> {
  const response = await apiRequest<CategoryListResponse>(categoriesPath);
  return response.items ?? [];
}

export async function saveCategory(category: CategorySummary): Promise<CategorySummary> {
  return apiRequest<CategorySummary>(`${categoriesPath}/${encodeURIComponent(category.id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(category)
  });
}

export async function deleteCategory(categoryId: string): Promise<void> {
  await apiRequest<void>(`${categoriesPath}/${encodeURIComponent(categoryId)}`, { method: 'DELETE' });
}

export async function listPropertyTypes(): Promise<PropertyTypeDefinition[]> {
  const response = await apiRequest<{ items?: PropertyTypeDefinition[] }>('/api/manage/property-types');
  return response.items ?? [];
}

/** TA-12: the picker renders from the resolver's own vocabulary, never a copy. */
export async function getVariableCatalogue(templateId?: string): Promise<VariableCatalogue> {
  const query = templateId ? `?templateId=${encodeURIComponent(templateId)}` : '';
  return apiRequest<VariableCatalogue>(`/api/manage/variables${query}`);
}

export async function listSystemProperties(): Promise<SystemPropertySummary[]> {
  const response = await apiRequest<{ items?: SystemPropertySummary[] }>('/api/manage/system-properties');
  return response.items ?? [];
}

/** Writes are admin-only; authors may read these to see what a token inherits. */
export async function saveSystemProperty(property: SystemPropertySummary): Promise<SystemPropertySummary> {
  return apiRequest<SystemPropertySummary>(
    `/api/manage/system-properties/${encodeURIComponent(property.tokenName)}`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(property) }
  );
}

export async function deleteSystemProperty(tokenName: string): Promise<void> {
  await apiRequest<void>(`/api/manage/system-properties/${encodeURIComponent(tokenName)}`, { method: 'DELETE' });
}

export async function saveTemplateProperties(
  templateId: string,
  properties: SavePropertyRequest[]
): Promise<TemplatePropertySummary[]> {
  const response = await apiRequest<{ properties?: TemplatePropertySummary[] }>(
    `${templatesPath}/${encodeURIComponent(templateId)}/properties`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ properties })
    }
  );
  return response.properties ?? [];
}
